import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { LuPencil } from 'react-icons/lu';
import { FiAlertCircle } from 'react-icons/fi';
import PregnancyModal from '../../ComponnentsUI/WorklistSearch/PregnancyModal';
import ClosePregnancyModal, {
  type ClosingStatus,
} from '../../ComponnentsUI/WorklistSearch/ClosePregnancyModal';
import { useReportData, type StudyHistoryEntry } from './lib/report-context';

function parseCalendarDate(raw?: string | null): Date | null {
  if (!raw) {
    return null;
  }
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(raw);
  if (isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function gaWeeksToParts(
  gaWeeks?: number | null
): { weeks: number; days: number; totalDays: number } | null {
  if (gaWeeks == null || !Number.isFinite(gaWeeks) || gaWeeks < 0) {
    return null;
  }
  const totalDays = Math.max(0, Math.round(gaWeeks * 7));
  return {
    weeks: Math.floor(totalDays / 7),
    days: totalDays % 7,
    totalDays,
  };
}

function formatGa(gaWeeks?: number | null): string {
  const parts = gaWeeksToParts(gaWeeks);
  if (!parts) {
    return '—';
  }
  return `${parts.weeks}w${parts.days}d`;
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) {
    return '—';
  }
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    return `${m[2]}/${m[3]}/${m[1]}`;
  }
  return '—';
}

function fmtCm(v?: number | null): string {
  if (v == null) {
    return '—';
  }
  return v.toFixed(1);
}

function fmtRatio(a?: number | null, b?: number | null): string {
  if (a == null || b == null || b === 0) {
    return '—';
  }
  return (a / b).toFixed(2);
}

type PRow = {
  ga: number;
  p3: number;
  p10: number;
  p50: number;
  p90: number;
  p97: number;
};

const hcRef: PRow[] = [
  { ga: 14, p3: 95, p10: 99, p50: 106, p90: 113, p97: 118 },
  { ga: 15, p3: 107, p10: 112, p50: 119, p90: 127, p97: 132 },
  { ga: 16, p3: 119, p10: 124, p50: 132, p90: 140, p97: 146 },
  { ga: 17, p3: 130, p10: 136, p50: 145, p90: 154, p97: 160 },
  { ga: 18, p3: 141, p10: 148, p50: 157, p90: 167, p97: 173 },
  { ga: 19, p3: 152, p10: 159, p50: 169, p90: 179, p97: 186 },
  { ga: 20, p3: 163, p10: 171, p50: 181, p90: 192, p97: 199 },
  { ga: 21, p3: 173, p10: 182, p50: 193, p90: 204, p97: 212 },
  { ga: 22, p3: 184, p10: 193, p50: 205, p90: 217, p97: 225 },
  { ga: 23, p3: 194, p10: 203, p50: 216, p90: 228, p97: 237 },
  { ga: 24, p3: 203, p10: 213, p50: 226, p90: 239, p97: 248 },
  { ga: 25, p3: 212, p10: 223, p50: 237, p90: 250, p97: 259 },
  { ga: 26, p3: 221, p10: 232, p50: 247, p90: 261, p97: 270 },
  { ga: 27, p3: 230, p10: 242, p50: 257, p90: 272, p97: 281 },
  { ga: 28, p3: 238, p10: 251, p50: 267, p90: 282, p97: 292 },
  { ga: 29, p3: 246, p10: 259, p50: 276, p90: 292, p97: 302 },
  { ga: 30, p3: 254, p10: 268, p50: 285, p90: 302, p97: 312 },
  { ga: 31, p3: 261, p10: 276, p50: 293, p90: 311, p97: 321 },
  { ga: 32, p3: 268, p10: 284, p50: 302, p90: 319, p97: 330 },
  { ga: 33, p3: 275, p10: 291, p50: 309, p90: 328, p97: 338 },
  { ga: 34, p3: 281, p10: 297, p50: 316, p90: 335, p97: 346 },
  { ga: 35, p3: 286, p10: 303, p50: 322, p90: 342, p97: 353 },
  { ga: 36, p3: 291, p10: 308, p50: 328, p90: 348, p97: 359 },
  { ga: 37, p3: 295, p10: 313, p50: 333, p90: 353, p97: 364 },
  { ga: 38, p3: 299, p10: 317, p50: 337, p90: 358, p97: 369 },
  { ga: 39, p3: 302, p10: 320, p50: 341, p90: 362, p97: 373 },
  { ga: 40, p3: 304, p10: 323, p50: 344, p90: 365, p97: 376 },
];

const acRef: PRow[] = [
  { ga: 14, p3: 72, p10: 77, p50: 85, p90: 93, p97: 98 },
  { ga: 15, p3: 84, p10: 89, p50: 98, p90: 107, p97: 113 },
  { ga: 16, p3: 95, p10: 101, p50: 111, p90: 121, p97: 127 },
  { ga: 17, p3: 106, p10: 113, p50: 124, p90: 135, p97: 142 },
  { ga: 18, p3: 117, p10: 124, p50: 137, p90: 149, p97: 156 },
  { ga: 19, p3: 128, p10: 136, p50: 149, p90: 163, p97: 170 },
  { ga: 20, p3: 139, p10: 148, p50: 162, p90: 176, p97: 185 },
  { ga: 21, p3: 149, p10: 159, p50: 174, p90: 189, p97: 199 },
  { ga: 22, p3: 160, p10: 170, p50: 186, p90: 202, p97: 213 },
  { ga: 23, p3: 170, p10: 181, p50: 198, p90: 215, p97: 226 },
  { ga: 24, p3: 180, p10: 192, p50: 210, p90: 228, p97: 240 },
  { ga: 25, p3: 190, p10: 203, p50: 222, p90: 241, p97: 253 },
  { ga: 26, p3: 200, p10: 213, p50: 233, p90: 253, p97: 266 },
  { ga: 27, p3: 209, p10: 223, p50: 244, p90: 265, p97: 279 },
  { ga: 28, p3: 219, p10: 233, p50: 255, p90: 277, p97: 292 },
  { ga: 29, p3: 228, p10: 244, p50: 267, p90: 290, p97: 305 },
  { ga: 30, p3: 237, p10: 254, p50: 278, p90: 302, p97: 317 },
  { ga: 31, p3: 246, p10: 263, p50: 288, p90: 313, p97: 329 },
  { ga: 32, p3: 255, p10: 273, p50: 299, p90: 325, p97: 341 },
  { ga: 33, p3: 263, p10: 282, p50: 309, p90: 336, p97: 353 },
  { ga: 34, p3: 271, p10: 291, p50: 319, p90: 347, p97: 364 },
  { ga: 35, p3: 279, p10: 300, p50: 328, p90: 357, p97: 375 },
  { ga: 36, p3: 286, p10: 308, p50: 337, p90: 367, p97: 386 },
  { ga: 37, p3: 292, p10: 315, p50: 346, p90: 376, p97: 396 },
  { ga: 38, p3: 298, p10: 322, p50: 353, p90: 384, p97: 405 },
  { ga: 39, p3: 303, p10: 328, p50: 360, p90: 392, p97: 413 },
  { ga: 40, p3: 307, p10: 332, p50: 365, p90: 398, p97: 420 },
];

const flRef: PRow[] = [
  { ga: 14, p3: 13, p10: 14, p50: 16, p90: 18, p97: 19 },
  { ga: 15, p3: 16, p10: 17, p50: 19, p90: 21, p97: 22 },
  { ga: 16, p3: 19, p10: 20, p50: 22, p90: 24, p97: 26 },
  { ga: 17, p3: 22, p10: 23, p50: 26, p90: 28, p97: 29 },
  { ga: 18, p3: 24, p10: 26, p50: 29, p90: 31, p97: 33 },
  { ga: 19, p3: 27, p10: 29, p50: 32, p90: 35, p97: 36 },
  { ga: 20, p3: 30, p10: 32, p50: 35, p90: 38, p97: 40 },
  { ga: 21, p3: 33, p10: 35, p50: 38, p90: 41, p97: 43 },
  { ga: 22, p3: 36, p10: 38, p50: 41, p90: 44, p97: 47 },
  { ga: 23, p3: 38, p10: 40, p50: 44, p90: 47, p97: 50 },
  { ga: 24, p3: 41, p10: 43, p50: 47, p90: 51, p97: 53 },
  { ga: 25, p3: 43, p10: 46, p50: 50, p90: 54, p97: 56 },
  { ga: 26, p3: 46, p10: 48, p50: 53, p90: 57, p97: 59 },
  { ga: 27, p3: 48, p10: 51, p50: 55, p90: 60, p97: 62 },
  { ga: 28, p3: 50, p10: 53, p50: 58, p90: 62, p97: 65 },
  { ga: 29, p3: 52, p10: 56, p50: 60, p90: 65, p97: 68 },
  { ga: 30, p3: 54, p10: 58, p50: 63, p90: 67, p97: 70 },
  { ga: 31, p3: 56, p10: 60, p50: 65, p90: 70, p97: 73 },
  { ga: 32, p3: 58, p10: 62, p50: 67, p90: 72, p97: 75 },
  { ga: 33, p3: 60, p10: 64, p50: 69, p90: 74, p97: 77 },
  { ga: 34, p3: 61, p10: 65, p50: 71, p90: 76, p97: 79 },
  { ga: 35, p3: 63, p10: 67, p50: 73, p90: 78, p97: 81 },
  { ga: 36, p3: 64, p10: 68, p50: 74, p90: 80, p97: 83 },
  { ga: 37, p3: 65, p10: 70, p50: 76, p90: 81, p97: 85 },
  { ga: 38, p3: 66, p10: 71, p50: 77, p90: 83, p97: 86 },
  { ga: 39, p3: 67, p10: 72, p50: 78, p90: 84, p97: 87 },
  { ga: 40, p3: 68, p10: 73, p50: 79, p90: 85, p97: 88 },
];

const bpdRef: PRow[] = [
  { ga: 14, p3: 26, p10: 27, p50: 29, p90: 31, p97: 32 },
  { ga: 15, p3: 29, p10: 30, p50: 32, p90: 34, p97: 35 },
  { ga: 16, p3: 32, p10: 33, p50: 35, p90: 37, p97: 39 },
  { ga: 17, p3: 35, p10: 37, p50: 39, p90: 41, p97: 43 },
  { ga: 18, p3: 38, p10: 40, p50: 43, p90: 45, p97: 47 },
  { ga: 19, p3: 42, p10: 44, p50: 46, p90: 49, p97: 51 },
  { ga: 20, p3: 45, p10: 47, p50: 50, p90: 53, p97: 55 },
  { ga: 21, p3: 48, p10: 51, p50: 54, p90: 57, p97: 59 },
  { ga: 22, p3: 52, p10: 54, p50: 57, p90: 60, p97: 63 },
  { ga: 23, p3: 55, p10: 58, p50: 61, p90: 64, p97: 67 },
  { ga: 24, p3: 58, p10: 61, p50: 64, p90: 68, p97: 70 },
  { ga: 25, p3: 61, p10: 64, p50: 68, p90: 72, p97: 74 },
  { ga: 26, p3: 64, p10: 67, p50: 71, p90: 75, p97: 78 },
  { ga: 27, p3: 67, p10: 71, p50: 75, p90: 79, p97: 82 },
  { ga: 28, p3: 70, p10: 74, p50: 78, p90: 82, p97: 85 },
  { ga: 29, p3: 73, p10: 77, p50: 81, p90: 86, p97: 89 },
  { ga: 30, p3: 76, p10: 80, p50: 84, p90: 89, p97: 92 },
  { ga: 31, p3: 78, p10: 82, p50: 87, p90: 92, p97: 95 },
  { ga: 32, p3: 81, p10: 85, p50: 90, p90: 95, p97: 98 },
  { ga: 33, p3: 83, p10: 87, p50: 92, p90: 97, p97: 101 },
  { ga: 34, p3: 85, p10: 89, p50: 94, p90: 100, p97: 103 },
  { ga: 35, p3: 87, p10: 91, p50: 96, p90: 102, p97: 106 },
  { ga: 36, p3: 88, p10: 93, p50: 98, p90: 104, p97: 108 },
  { ga: 37, p3: 89, p10: 94, p50: 99, p90: 105, p97: 109 },
  { ga: 38, p3: 90, p10: 95, p50: 100, p90: 106, p97: 110 },
  { ga: 39, p3: 91, p10: 96, p50: 101, p90: 107, p97: 111 },
  { ga: 40, p3: 91, p10: 96, p50: 102, p90: 108, p97: 112 },
];

const efwRef: PRow[] = [
  { ga: 14, p3: 60, p10: 70, p50: 85, p90: 103, p97: 115 },
  { ga: 15, p3: 80, p10: 93, p50: 113, p90: 136, p97: 152 },
  { ga: 16, p3: 105, p10: 120, p50: 146, p90: 176, p97: 197 },
  { ga: 17, p3: 133, p10: 152, p50: 185, p90: 222, p97: 249 },
  { ga: 18, p3: 167, p10: 192, p50: 232, p90: 279, p97: 312 },
  { ga: 19, p3: 207, p10: 238, p50: 287, p90: 346, p97: 386 },
  { ga: 20, p3: 255, p10: 293, p50: 354, p90: 426, p97: 475 },
  { ga: 21, p3: 311, p10: 357, p50: 431, p90: 519, p97: 579 },
  { ga: 22, p3: 376, p10: 433, p50: 522, p90: 628, p97: 701 },
  { ga: 23, p3: 453, p10: 521, p50: 628, p90: 756, p97: 843 },
  { ga: 24, p3: 540, p10: 621, p50: 749, p90: 901, p97: 1005 },
  { ga: 25, p3: 640, p10: 737, p50: 888, p90: 1068, p97: 1191 },
  { ga: 26, p3: 755, p10: 868, p50: 1045, p90: 1258, p97: 1402 },
  { ga: 27, p3: 885, p10: 1019, p50: 1224, p90: 1472, p97: 1641 },
  { ga: 28, p3: 1033, p10: 1188, p50: 1428, p90: 1717, p97: 1914 },
  { ga: 29, p3: 1200, p10: 1379, p50: 1655, p90: 1990, p97: 2218 },
  { ga: 30, p3: 1385, p10: 1592, p50: 1912, p90: 2299, p97: 2563 },
  { ga: 31, p3: 1588, p10: 1828, p50: 2196, p90: 2640, p97: 2944 },
  { ga: 32, p3: 1810, p10: 2083, p50: 2502, p90: 3009, p97: 3355 },
  { ga: 33, p3: 2046, p10: 2356, p50: 2830, p90: 3402, p97: 3796 },
  { ga: 34, p3: 2289, p10: 2638, p50: 3170, p90: 3812, p97: 4255 },
  { ga: 35, p3: 2529, p10: 2918, p50: 3510, p90: 4224, p97: 4715 },
  { ga: 36, p3: 2754, p10: 3178, p50: 3824, p90: 4600, p97: 5137 },
  { ga: 37, p3: 2952, p10: 3409, p50: 4103, p90: 4939, p97: 5519 },
  { ga: 38, p3: 3115, p10: 3602, p50: 4340, p90: 5225, p97: 5841 },
  { ga: 39, p3: 3235, p10: 3750, p50: 4520, p90: 5443, p97: 6085 },
  { ga: 40, p3: 3310, p10: 3847, p50: 4640, p90: 5592, p97: 6253 },
];

// ─── Percentile computation from p3/p10/p50/p90/p97 breakpoints ──

/**
 * Interpolate a numeric percentile from the 5-point reference breakpoints.
 * Returns a value between 0 and 100.
 */
function interpolatePercentile(row: PRow, value: number): number {
  const pts: [number, number][] = [
    [row.p3, 3],
    [row.p10, 10],
    [row.p50, 50],
    [row.p90, 90],
    [row.p97, 97],
  ];
  if (value <= pts[0][0]) {
    return Math.max(1, Math.round(3 * (value / pts[0][0])));
  }
  if (value >= pts[4][0]) {
    return Math.min(
      99,
      Math.round(97 + 3 * ((value - pts[4][0]) / (pts[4][0] * 0.05 || 1)))
    );
  }
  for (let i = 0; i < pts.length - 1; i++) {
    if (value >= pts[i][0] && value <= pts[i + 1][0]) {
      const frac = (value - pts[i][0]) / (pts[i + 1][0] - pts[i][0]);
      return Math.round(pts[i][1] + frac * (pts[i + 1][1] - pts[i][1]));
    }
  }
  return 50;
}

function getRefRow(table: PRow[], ga: number): PRow | null {
  const lo = table.find((r) => r.ga === Math.floor(ga));
  const hi = table.find((r) => r.ga === Math.ceil(ga));
  if (!lo && !hi) {
    return null;
  }
  if (!lo) {
    return hi!;
  }
  if (!hi || lo.ga === hi.ga) {
    return lo;
  }
  const t = ga - lo.ga;
  return {
    ga: ga,
    p3: lo.p3 + t * (hi.p3 - lo.p3),
    p10: lo.p10 + t * (hi.p10 - lo.p10),
    p50: lo.p50 + t * (hi.p50 - lo.p50),
    p90: lo.p90 + t * (hi.p90 - lo.p90),
    p97: lo.p97 + t * (hi.p97 - lo.p97),
  };
}

/** Compute percentile for a biometry value (cm → mm) or EFW (g) against a reference table. */
function bioPct(
  valueCm: number | null,
  gaWeeks: number | null,
  table: PRow[],
  toMm = true
): string {
  if (valueCm == null || gaWeeks == null) {
    return '—';
  }
  const row = getRefRow(table, gaWeeks);
  if (!row) {
    return '—';
  }
  const v = toMm ? valueCm * 10 : valueCm;
  return `${interpolatePercentile(row, v)}%`;
}

function efwPercentile(efwG: number | null, gaWeeks: number | null): string {
  return bioPct(efwG, gaWeeks, efwRef, false);
}

function hcAcPercentile(
  hcCm: number | null,
  acCm: number | null,
  gaWeeks: number | null
): string {
  if (hcCm == null || acCm == null || gaWeeks == null) {
    return '—';
  }
  const hcPct = bioPct(hcCm, gaWeeks, hcRef);
  const acPct = bioPct(acCm, gaWeeks, acRef);
  if (hcPct === '—' || acPct === '—') {
    return '—';
  }
  const hcN = parseInt(hcPct, 10);
  const acN = parseInt(acPct, 10);
  const diff = hcN - acN;
  return `${Math.max(1, Math.min(99, 50 + diff))}%`;
}

function flAcPercentile(
  flCm: number | null,
  acCm: number | null,
  gaWeeks: number | null
): string {
  if (flCm == null || acCm == null || gaWeeks == null) {
    return '—';
  }
  const flPct = bioPct(flCm, gaWeeks, flRef);
  const acPct = bioPct(acCm, gaWeeks, acRef);
  if (flPct === '—' || acPct === '—') {
    return '—';
  }
  const flN = parseInt(flPct, 10);
  const acN = parseInt(acPct, 10);
  const diff = flN - acN;
  return `${Math.max(1, Math.min(99, 50 + diff))}%`;
}

function flBpdPercentile(
  flCm: number | null,
  bpdCm: number | null,
  gaWeeks: number | null
): string {
  if (flCm == null || bpdCm == null || gaWeeks == null) {
    return '—';
  }
  const flPct = bioPct(flCm, gaWeeks, flRef);
  const bpdPctStr = bioPct(bpdCm, gaWeeks, bpdRef);
  if (flPct === '—' || bpdPctStr === '—') {
    return '—';
  }
  const flN = parseInt(flPct, 10);
  const bpdN = parseInt(bpdPctStr, 10);
  const diff = flN - bpdN;
  return `${Math.max(1, Math.min(99, 50 + diff))}%`;
}

function ciValue(bpdCm: number | null, hcCm: number | null): string {
  if (bpdCm == null || hcCm == null || hcCm === 0) {
    return '—';
  }
  const ofd = (2 * hcCm) / Math.PI - bpdCm;
  if (ofd <= 0) {
    return '—';
  }
  return (bpdCm / ofd).toFixed(2);
}

function ciPercentile(
  bpdCm: number | null,
  hcCm: number | null,
  gaWeeks: number | null
): string {
  if (bpdCm == null || hcCm == null || gaWeeks == null) {
    return '—';
  }
  const bpdPctStr = bioPct(bpdCm, gaWeeks, bpdRef);
  const hcPctStr = bioPct(hcCm, gaWeeks, hcRef);
  if (bpdPctStr === '—' || hcPctStr === '—') {
    return '—';
  }
  const bpdN = parseInt(bpdPctStr, 10);
  const hcN = parseInt(hcPctStr, 10);
  const diff = bpdN - hcN;
  return `${Math.max(1, Math.min(99, 50 + diff))}%`;
}

/** Returns a td class that turns red when the percentile is < 5% or > 95%. */
function pctClass(pctStr: string): string {
  const base =
    'px-3 py-1.5 text-xs border-b border-slate-100 dark:border-gray-700/50 text-center whitespace-nowrap';
  if (pctStr === '—') {
    return `${base} text-slate-700 dark:text-gray-300`;
  }
  const n = parseInt(pctStr, 10);
  if (n <= 5 || n >= 95) {
    return `${base} font-bold text-red-600 dark:text-red-400`;
  }
  return `${base} text-slate-700 dark:text-gray-300`;
}

const th =
  'px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-400 bg-slate-50 dark:bg-gray-800/60 border-b border-slate-200 dark:border-gray-700 text-center whitespace-nowrap';

const td =
  'px-3 py-1.5 text-xs text-slate-700 dark:text-gray-300 border-b border-slate-100 dark:border-gray-700/50 text-center whitespace-nowrap';

const tdBold =
  'px-3 py-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300 bg-blue-50/50 dark:bg-blue-900/20 border-b border-slate-100 dark:border-gray-700/50 text-center whitespace-nowrap';

const tdLabel =
  'px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-400 border-b border-slate-100 dark:border-gray-700/50 text-left whitespace-nowrap';

function deriveLmpEdd(
  gaWeeks?: number | null,
  studyDateStr?: string | null
): { lmp: Date; edd: Date } | null {
  const parts = gaWeeksToParts(gaWeeks);
  if (!parts) {
    return null;
  }
  const base = parseCalendarDate(studyDateStr) ?? new Date();
  const lmp = new Date(base.getTime() - parts.totalDays * 24 * 60 * 60 * 1000);
  const edd = new Date(lmp.getTime() + 280 * 24 * 60 * 60 * 1000);
  return { lmp, edd };
}

function dateToDisplay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${day}/${y}`;
}

interface HistoryRow {
  index: number;
  uid: string;
  date: string | null;
  bpd: number | null;
  hc: number | null;
  ac: number | null;
  fl: number | null;
  gaAua: number | null;
  efwG: number | null;
  gaBpd: number | null;
  gaHc: number | null;
  gaAc: number | null;
  gaFl: number | null;
  lmp: string | null;
  edd: string | null;
  isCurrent: boolean;
}

function buildRows(
  history: StudyHistoryEntry[],
  currentUid?: string
): HistoryRow[] {
  const sorted = [...history].sort((a, b) => {
    if (!a.study_date && !b.study_date) {
      return 0;
    }
    if (!a.study_date) {
      return -1;
    }
    if (!b.study_date) {
      return 1;
    }
    return new Date(a.study_date).getTime() - new Date(b.study_date).getTime();
  });

  return sorted.map((entry, i) => {
    const meas = entry.study_report?.measurements;
    const derived = deriveLmpEdd(meas?.ga_aua, entry.study_date);
    return {
      index: i + 1,
      uid: entry.study_instance_uid,
      date: entry.study_date,
      bpd: meas?.bpd_cm ?? null,
      hc: meas?.hc_cm ?? null,
      ac: meas?.ac_cm ?? null,
      fl: meas?.fl_cm ?? null,
      gaAua: meas?.ga_aua ?? null,
      efwG: meas?.efw_g ?? null,
      gaBpd: meas?.ga_bpd ?? null,
      gaHc: meas?.ga_hc ?? null,
      gaAc: meas?.ga_ac ?? null,
      gaFl: meas?.ga_fl ?? null,
      lmp: derived ? dateToDisplay(derived.lmp) : null,
      edd: derived ? dateToDisplay(derived.edd) : null,
      isCurrent: entry.study_instance_uid === currentUid,
    };
  });
}

function SummaryCard({
  onEditPregnancy,
  onClosePregnancy,
  canClosePregnancy,
}: {
  onEditPregnancy: () => void;
  onClosePregnancy?: () => void;
  canClosePregnancy?: boolean;
}) {
  const { t } = useTranslation('Report');
  const {
    biometryValues,
    efw,
    gaPredictions,
    pregnancyInfo,
    studyInfo,
    currentLmp,
    currentEdd,
    currentGaWeeks,
    currentGaDays,
  } = useReportData();

  const datingLabel = pregnancyInfo?.datingMethod || 'AUA';

  const derived = deriveLmpEdd(gaPredictions.predGaAua, studyInfo?.studyDate);
  const lmpDisplay = derived ? dateToDisplay(derived.lmp) : '—';
  const eddDisplay = derived ? dateToDisplay(derived.edd) : '—';

  const statedLmp = currentLmp ? formatDate(currentLmp.split('T')[0]) : '—';
  const statedEdd = currentEdd ? formatDate(currentEdd.split('T')[0]) : '—';
  const gaWeeks = currentGaWeeks;
  const gaDays = currentGaDays;

  const measures = [
    { key: 'BPD', meas: biometryValues.bpd, ga: gaPredictions.predGaBpd },
    { key: 'HC', meas: biometryValues.hc, ga: gaPredictions.predGaHc },
    { key: 'AC', meas: biometryValues.ac, ga: gaPredictions.predGaAc },
    { key: 'FL', meas: biometryValues.fl, ga: gaPredictions.predGaFl },
  ];

  return (
    <section>
      {/* Stated LMP row */}
      <div className="flex items-center gap-3 mb-3 px-3 py-2 bg-slate-50 dark:bg-gray-800/60 border border-slate-200 dark:border-gray-700 rounded-lg">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-gray-500 shrink-0">
          {t('dating.statedLmp')}
        </span>
        <span className="text-xs font-semibold text-slate-700 dark:text-gray-200">
          {statedLmp}
        </span>
        <span className="h-3 w-px bg-slate-200 dark:bg-gray-700" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-gray-500 shrink-0">
          {t('dating.edd')}
        </span>
        <span className="text-xs font-semibold text-slate-700 dark:text-gray-200">
          {statedEdd}
        </span>
        {gaWeeks != null && (
          <>
            <span className="h-3 w-px bg-slate-200 dark:bg-gray-700" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-gray-500 shrink-0">
              {t('dating.ga')}
            </span>
            <span className="text-xs font-semibold text-slate-700 dark:text-gray-200">
              {gaWeeks}w{gaDays ?? 0}d
            </span>
          </>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={onEditPregnancy}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
          >
            <LuPencil size={11} />
            {t('dating.edit')}
          </button>
          {canClosePregnancy && onClosePregnancy ? (
            <button
              type="button"
              onClick={onClosePregnancy}
              className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-title-page-color-300 transition-colors hover:bg-card-color-200 dark:text-gray-400 dark:hover:bg-gray-800/60"
              title={t('dating.closeTooltip')}
            >
              <FiAlertCircle size={12} />
              {t('dating.close')}
            </button>
          ) : null}
        </div>
      </div>

      {/* Summary badges */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-gray-500">
            {t('dating.bestGa')}
          </span>
          <span className="text-sm font-bold text-slate-800 dark:text-gray-100">
            {formatGa(gaPredictions.predGaAua)}
          </span>
        </div>
        <span className="h-4 w-px bg-slate-200 dark:bg-gray-700" />
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-gray-500">
            {t('dating.dating')}
          </span>
          <span className="text-xs font-semibold text-slate-700 dark:text-gray-300">
            {datingLabel}
          </span>
        </div>
        <span className="h-4 w-px bg-slate-200 dark:bg-gray-700" />
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-gray-500">
            {t('dating.lmpUs')}
          </span>
          <span className="text-xs font-semibold text-slate-700 dark:text-gray-300">
            {lmpDisplay}
          </span>
        </div>
        <span className="h-4 w-px bg-slate-200 dark:bg-gray-700" />
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-gray-500">
            {t('dating.eddUs')}
          </span>
          <span className="text-xs font-semibold text-slate-700 dark:text-gray-300">
            {eddDisplay}
          </span>
        </div>
        {efw.efwGrams != null && (
          <>
            <span className="h-4 w-px bg-slate-200 dark:bg-gray-700" />
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-gray-500">
                {t('dating.efw')}
              </span>
              <span className="text-xs font-semibold text-slate-700 dark:text-gray-300">
                {Math.round(efw.efwGrams)} g
              </span>
            </div>
          </>
        )}
      </div>

      {/* Current biometry & per-measurement GA */}
      <div className="border border-slate-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr>
              <th className={th} />
              {measures.map((m) => (
                <th key={m.key} className={th}>
                  {m.key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={tdLabel}>{t('dating.measCm')}</td>
              {measures.map((m) => (
                <td key={m.key} className={td}>
                  {fmtCm(m.meas)}
                </td>
              ))}
            </tr>
            <tr>
              <td className={tdLabel}>{t('dating.predGa')}</td>
              {measures.map((m) => (
                <td key={m.key} className={tdBold}>
                  {formatGa(m.ga)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GaAnalysisTable({ rows }: { rows: HistoryRow[] }) {
  const { t } = useTranslation('Report');
  return (
    <section>
      <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-3">
        {t('dating.gaAnalysis')}
      </h3>
      <div className="border border-slate-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr>
              <th className={th}>#</th>
              <th className={th}>{t('dating.date')}</th>
              <th className={th}>BPD (cm)</th>
              <th className={th}>HC (cm)</th>
              <th className={th}>AC (cm)</th>
              <th className={th}>FL (cm)</th>
              <th className={th}>{t('dating.gaBpd')}</th>
              <th className={th}>{t('dating.gaHc')}</th>
              <th className={th}>{t('dating.gaAc')}</th>
              <th className={th}>{t('dating.gaFl')}</th>
              <th className={th}>{t('dating.estGa')}</th>
              <th className={th}>{t('dating.lmp')}</th>
              <th className={th}>{t('dating.edd')}</th>
              <th className={th}>{t('dating.efwG')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={14} className={`${td} text-slate-400 py-6`}>
                  {t('dating.noHistory')}
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const rowBg = row.isCurrent
                ? 'bg-blue-50/60 dark:bg-blue-900/15'
                : '';
              return (
                <tr key={row.uid} className={rowBg}>
                  <td className={td}>
                    {row.index}
                    {row.isCurrent ? ' ★' : ''}
                  </td>
                  <td className={td}>{formatDate(row.date)}</td>
                  <td className={td}>{fmtCm(row.bpd)}</td>
                  <td className={td}>{fmtCm(row.hc)}</td>
                  <td className={td}>{fmtCm(row.ac)}</td>
                  <td className={td}>{fmtCm(row.fl)}</td>
                  <td className={td}>{formatGa(row.gaBpd)}</td>
                  <td className={td}>{formatGa(row.gaHc)}</td>
                  <td className={td}>{formatGa(row.gaAc)}</td>
                  <td className={td}>{formatGa(row.gaFl)}</td>
                  <td className={row.isCurrent ? tdBold : td}>
                    {formatGa(row.gaAua)}
                  </td>
                  <td className={td}>{row.lmp ?? '—'}</td>
                  <td className={td}>{row.edd ?? '—'}</td>
                  <td className={td}>
                    {row.efwG != null ? Math.round(row.efwG) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GrowthRatiosTable({ rows }: { rows: HistoryRow[] }) {
  const { t } = useTranslation('Report');
  return (
    <section>
      <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-3">
        {t('dating.growthRatios')}
      </h3>
      <div className="border border-slate-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr>
              <th className={th}>#</th>
              <th className={th}>{t('dating.date')}</th>
              <th className={th}>HC/AC</th>
              <th className={th}>(%)</th>
              <th className={th}>FL/AC</th>
              <th className={th}>(%)</th>
              <th className={th}>CI</th>
              <th className={th}>(%)</th>
              <th className={th}>FL/BPD</th>
              <th className={th}>(%)</th>
              <th className={th}>{t('dating.estFw')}</th>
              <th className={th}>(%)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={12} className={`${td} text-slate-400 py-6`}>
                  {t('dating.noHistory')}
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const rowBg = row.isCurrent
                ? 'bg-blue-50/60 dark:bg-blue-900/15'
                : '';
              const pctHcAc = hcAcPercentile(row.hc, row.ac, row.gaAua);
              const pctFlAc = flAcPercentile(row.fl, row.ac, row.gaAua);
              const pctCi = ciPercentile(row.bpd, row.hc, row.gaAua);
              const pctFlBpd = flBpdPercentile(row.fl, row.bpd, row.gaAua);
              const pctEfw = efwPercentile(row.efwG, row.gaAua);
              return (
                <tr key={row.uid} className={rowBg}>
                  <td className={td}>
                    {row.index}
                    {row.isCurrent ? ' ★' : ''}
                  </td>
                  <td className={td}>{formatDate(row.date)}</td>
                  <td className={td}>{fmtRatio(row.hc, row.ac)}</td>
                  <td className={pctClass(pctHcAc)}>{pctHcAc}</td>
                  <td className={td}>{fmtRatio(row.fl, row.ac)}</td>
                  <td className={pctClass(pctFlAc)}>{pctFlAc}</td>
                  <td className={td}>{ciValue(row.bpd, row.hc)}</td>
                  <td className={pctClass(pctCi)}>{pctCi}</td>
                  <td className={td}>{fmtRatio(row.fl, row.bpd)}</td>
                  <td className={pctClass(pctFlBpd)}>{pctFlBpd}</td>
                  <td className={row.isCurrent ? tdBold : td}>
                    {row.efwG != null ? Math.round(row.efwG) : '—'}
                  </td>
                  <td className={pctClass(pctEfw)}>{pctEfw}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function DatingFetalGrowth() {
  const { t } = useTranslation('Report');
  const {
    studyInfo,
    pregnancyStudiesHistory,
    pregnancyInfo,
    patientInfo,
    patientId,
    onPregnancyUpdate,
    currentPregnancyId,
    currentLmp,
    currentEdd,
    currentGaWeeks,
    currentGaDays,
  } = useReportData();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitialData, setModalInitialData] = useState<any>(undefined);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeInitialStatus, setCloseInitialStatus] = useState<
    ClosingStatus | undefined
  >(undefined);

  const rows = useMemo(
    () => buildRows(pregnancyStudiesHistory, studyInfo?.studyId),
    [pregnancyStudiesHistory, studyInfo?.studyId]
  );

  const resolvedPregnancyId = pregnancyInfo?.id ?? currentPregnancyId;

  const activePregnancyId = useMemo<number | null>(() => {
    if (!resolvedPregnancyId) return null;
    const status = String(pregnancyInfo?.pregnancyStatus ?? 'Active');
    return status === 'Active' ? resolvedPregnancyId : null;
  }, [resolvedPregnancyId, pregnancyInfo?.pregnancyStatus]);

  const canClosePregnancy = Boolean(patientId) && activePregnancyId !== null;

  const handleEditPregnancy = () => {
    if (!resolvedPregnancyId) {
      return;
    }

    const lmp = currentLmp ?? pregnancyInfo?.lastMenstrualPeriod;
    const edd = currentEdd ?? pregnancyInfo?.estimatedDueDate;

    const w = currentGaWeeks ?? 0;
    const d = currentGaDays ?? 0;
    let trimester: 'T1' | 'T2' | 'T3' = 'T1';
    if (w >= 28) {
      trimester = 'T3';
    } else if (w >= 14) {
      trimester = 'T2';
    }

    type PregnancyStatus =
      | 'Active'
      | 'Delivered'
      | 'Miscarried'
      | 'Terminated'
      | 'Unknown';
    const validStatuses: PregnancyStatus[] = [
      'Active',
      'Delivered',
      'Miscarried',
      'Terminated',
      'Unknown',
    ];
    const rawStatus = pregnancyInfo?.pregnancyStatus ?? 'Unknown';
    const status: PregnancyStatus = validStatuses.includes(
      rawStatus as PregnancyStatus
    )
      ? (rawStatus as PregnancyStatus)
      : 'Active';

    setModalInitialData({
      lmp: lmp && lmp !== 'Unknown' ? dayjs(lmp).format('YYYY-MM-DD') : '',
      edd: edd && edd !== 'Unknown' ? dayjs(edd).format('YYYY-MM-DD') : '',
      gaWeeks: w > 0 ? w.toString() : '',
      gaDays: d > 0 ? d.toString() : '',
      crl: pregnancyInfo?.preg_crl?.toString() ?? '',
      bpd: pregnancyInfo?.preg_bpd?.toString() ?? '',
      hc: pregnancyInfo?.preg_hc?.toString() ?? '',
      ac: pregnancyInfo?.preg_ac?.toString() ?? '',
      fl: pregnancyInfo?.preg_fl?.toString() ?? '',
      grossesse_classee_a_risque:
        pregnancyInfo?.grossesse_classee_a_risque ?? null,
      risque_anemie: pregnancyInfo?.risque_anemie === true,
      risque_hta: pregnancyInfo?.risque_hta === true,
      risque_diabete: pregnancyInfo?.risque_diabete === true,
      risque_cardiopathie: pregnancyInfo?.risque_cardiopathie === true,
      risque_metrorragie: pregnancyInfo?.risque_metrorragie === true,
      risque_infection: pregnancyInfo?.risque_infection === true,
      risque_preeclampsie: pregnancyInfo?.risque_preeclampsie === true,
      risque_eclampsie: pregnancyInfo?.risque_eclampsie === true,
      risque_autre_details: pregnancyInfo?.risque_autre_details ?? '',
      status,
      trimester,
    });
    setModalOpen(true);
  };

  const openClosePregnancyModal = (initial?: ClosingStatus) => {
    if (!activePregnancyId) {
      return;
    }
    setCloseInitialStatus(initial);
    setCloseModalOpen(true);
  };

  const handleCloseClosePregnancyModal = () => {
    setCloseModalOpen(false);
    setCloseInitialStatus(undefined);
  };

  const handleRequestClosePregnancy = (status: string) => {
    setModalOpen(false);
    if (
      status === 'Delivered' ||
      status === 'Terminated' ||
      status === 'Miscarried' ||
      status === 'Unknown'
    ) {
      openClosePregnancyModal(status as ClosingStatus);
    }
  };

  const handlePregnancyClosed = (finalStatus: ClosingStatus) => {
    onPregnancyUpdate?.({ pregnancy_status: finalStatus });
  };

  const worklistItem: any = patientId
    ? {
        mwl: {
          PatientName: patientInfo?.name ?? '',
          PatientID: patientId,
          PatientBirthDate: patientInfo?.birthday
            ? dayjs(patientInfo.birthday).format('YYYYMMDD')
            : '',
          PatientSex: patientInfo?.sex ?? 'F',
        },
        resolution: {
          patient_exists: true,
          patient_uuid: patientId,
          patient_id_internal: patientId,
          active_pregnancy_exists: pregnancyInfo?.pregnancyStatus === 'Active',
          pregnancy: pregnancyInfo,
          study_exists: false,
          study_instance_uid: null,
        },
      }
    : null;

  return (
    <>
      <PregnancyModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        worklistItem={worklistItem}
        mode="edit-backend"
        pregnancyId={resolvedPregnancyId}
        studyDate={
          studyInfo?.studyDate !== 'Unknown' ? studyInfo?.studyDate : undefined
        }
        ultrasoundDate={(() => {
          const raw = (
            pregnancyInfo as { ultrasound_date?: string } | undefined
          )?.ultrasound_date;
          if (!raw) return undefined;
          const d = dayjs(raw);
          return d.isValid() ? d.format('YYYY-MM-DD') : undefined;
        })()}
        initialData={modalInitialData}
        onUpdateSuccess={(updated: any) => {
          const data: Record<string, unknown> = updated?.data ?? updated;
          onPregnancyUpdate?.(data);
          setModalOpen(false);
        }}
        onRequestClosePregnancy={
          activePregnancyId !== null ? handleRequestClosePregnancy : undefined
        }
      />

      {patientId && activePregnancyId !== null ? (
        <ClosePregnancyModal
          isOpen={closeModalOpen}
          onClose={handleCloseClosePregnancyModal}
          patientId={String(patientId)}
          pregnancyId={activePregnancyId}
          initialStatus={closeInitialStatus}
          onClosed={handlePregnancyClosed}
        />
      ) : null}

      <div className="p-4 space-y-6 overflow-x-auto">
        <h2 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide flex items-center gap-2">
          <span className="w-1 h-4 bg-blue-500 rounded-full" />
          {t('dating.title')}
        </h2>
        <SummaryCard
          onEditPregnancy={handleEditPregnancy}
          onClosePregnancy={() => openClosePregnancyModal()}
          canClosePregnancy={canClosePregnancy}
        />
        <GaAnalysisTable rows={rows} />
        <GrowthRatiosTable rows={rows} />
      </div>
    </>
  );
}
